import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { createHash } from "crypto";

type Severity = "info" | "warn" | "error" | "critical";
type Source = "client" | "server" | "auth" | "business";

type IncidentInput = {
  severity?: Severity;
  source?: Source;
  event_type: string;
  message: string;
  route?: string | null;
  // user_id intentionally omitted: attribution is derived server-side to prevent log forgery.
  metadata?: Record<string, unknown>;
};

const ALLOWED_SEV: Severity[] = ["info", "warn", "error", "critical"];
const ALLOWED_SRC: Source[] = ["client", "server", "auth", "business"];

function truncate(v: unknown, max: number): string {
  const s = typeof v === "string" ? v : String(v ?? "");
  return s.length > max ? s.slice(0, max) : s;
}

export const logIncidentFn = createServerFn({ method: "POST" })
  .inputValidator((input: IncidentInput) => {
    if (!input || typeof input !== "object") throw new Error("Invalid input");
    if (!input.event_type || !input.message) throw new Error("event_type and message required");
    return input;
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const severity: Severity = ALLOWED_SEV.includes(data.severity as Severity)
      ? (data.severity as Severity)
      : "error";
    const source: Source = ALLOWED_SRC.includes(data.source as Source)
      ? (data.source as Source)
      : "client";

    const userAgent = truncate(getRequestHeader("user-agent") ?? "", 500);
    const ip = getRequestIP({ xForwardedFor: true }) ?? "";
    const ipHash = ip
      ? createHash("sha256")
          .update(ip + (process.env.SUPABASE_URL ?? ""))
          .digest("hex")
          .slice(0, 32)
      : null;

    const eventType = truncate(data.event_type, 120);
    const message = truncate(data.message, 2000);
    const route = data.route ? truncate(data.route, 300) : null;

    // Fingerprint groups near-duplicates for rate limiting
    const fingerprint = createHash("sha256")
      .update(`${source}|${eventType}|${message.slice(0, 200)}|${ipHash ?? ""}`)
      .digest("hex")
      .slice(0, 32);

    const metadata = data.metadata && typeof data.metadata === "object" ? data.metadata : {};

    const { error } = await supabaseAdmin.from("incident_logs").insert({
      severity,
      source,
      event_type: eventType,
      message,
      route,
      user_id: null,
      user_agent: userAgent || null,
      ip_hash: ipHash,
      fingerprint,
      metadata: metadata as never,
    });

    if (error) {
      console.error("[incident-log] insert failed:", error.message);
      return { ok: false };
    }
    return { ok: true };
  });
